/**
 * The table: one page of policies, whichever engine found them.
 *
 * The title is the row's one link. A link can be opened in a new tab, reached
 * by keyboard and read out as a link, none of which a clickable row manages.
 * Status is always words; the color only repeats them.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Highlight } from '../../search/HighlightText';
import { formatDate } from '../policyTable';
import type { Column } from './libraryColumns';
import type { LibraryRow } from './libraryPage';
import { ownerText, rowStatus } from './libraryText';
import type { LibraryLinkState } from './useLibrary';
import styles from './Library.module.css';

const SOURCE: Record<string, string> = { content: 'in the policy text', attachment: 'in an attachment', summary: 'in the summary' };

function Cell({ column, row, now, to, linkState }: { column: Column; row: LibraryRow; now: Date; to: string; linkState: LibraryLinkState }): ReactNode {
  switch (column.key) {
    case 'number':
      return <td className={styles.num} title={row.policyNumber || undefined}>{row.policyNumber || <span className={styles.absent}>—</span>}</td>;
    case 'title':
      return (
        <td>
          <Link to={to} state={linkState} className={styles.rowLink}>
            {row.match?.title ? <Highlight value={row.match.title} /> : row.title}
          </Link>
          {row.match?.snippet && (
            <p className={styles.snippet}>
              <Highlight value={row.match.snippet} />{' '}
              <span className={styles.snippetSource}>({SOURCE[row.match.source] ?? 'matched'})</span>
            </p>
          )}
        </td>
      );
    case 'department':
      return <td className={styles.cellMuted}>{row.departmentNames.length ? row.departmentNames.join(', ') : <span className={styles.absent}>—</span>}</td>;
    case 'documentType':
      return <td className={styles.cellMuted}>{row.documentTypeName ?? <span className={styles.absent}>—</span>}</td>;
    case 'owner': {
      const o = ownerText(row.owner);
      return (
        <td>
          <span className={o.tone === 'warn' ? styles.cellWarn : undefined}>{o.text}</span>
          {o.note && <span className={styles.cellNote}>{o.note}</span>}
        </td>
      );
    }
    case 'status': {
      const s = rowStatus(row, now);
      return (
        <td>
          <span className={`${styles.tag} ${styles[s.tone]}`}>{s.label}</span>
          {s.detail && <span className={styles.cellNote}>{s.detail}</span>}
        </td>
      );
    }
    case 'review':
      return <td className={`tnum ${row.reviewDate ? '' : styles.absent}`}>{formatDate(row.reviewDate)}</td>;
  }
}

export function LibraryTable({ columns, rows, now, search, linkState, updating, caption }: {
  columns: Column[];
  rows: LibraryRow[];
  now: Date;
  /** The view as it reads in the address, carried onto each policy's link so its back arrow returns here. */
  search: string;
  linkState: LibraryLinkState;
  /** The last answer is still showing while the next arrives. */
  updating: boolean;
  caption: string;
}) {
  return (
    <div className={updating ? `${styles.tableWrap} ${styles.updating}` : styles.tableWrap} aria-busy={updating || undefined}>
      <table className={styles.table}>
        <caption className="vh">{caption}</caption>
        <colgroup>
          {columns.map((c) => <col key={c.key} style={c.width ? { width: c.width } : undefined} />)}
        </colgroup>
        <thead>
          <tr>{columns.map((c) => <th key={c.key} scope="col">{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              {columns.map((c) => (
                <Cell key={c.key} column={c} row={row} now={now} linkState={linkState} to={`/policy/${row.id}${search ? `?${search}` : ''}`} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
